import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import IdeaCard from "@/components/ideas/IdeaCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

interface SupportingTabProps {
  userId: string;
  onCountChange?: (count: number) => void;
}

const SupportingTab = ({ userId, onCountChange }: SupportingTabProps) => {
  const [ideas, setIdeas] = useState<any[] | null>(null);
  const [votedIds, setVotedIds] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    const { data: votes } = await supabase
      .from("idea_votes")
      .select("idea_id")
      .eq("user_id", userId);
    const ids = (votes ?? []).map((v) => v.idea_id);
    setVotedIds(new Set(ids));

    if (ids.length === 0) {
      setIdeas([]);
      onCountChange?.(0);
      return;
    }

    const { data: ideasData } = await supabase
      .from("ideas")
      .select("*")
      .in("id", ids)
      .order("created_at", { ascending: false });
    const list = ideasData ?? [];

    // Attach author profiles in one batch
    const authorIds = Array.from(new Set(list.map((i) => i.user_id)));
    const { data: profiles } = await supabase
      .from("profiles")
      .select("user_id, display_name, avatar_url")
      .in("user_id", authorIds);
    const profileMap = new Map((profiles ?? []).map((p) => [p.user_id, p]));
    const enriched = list.map((i) => ({ ...i, profiles: profileMap.get(i.user_id) ?? null }));

    setIdeas(enriched);
    onCountChange?.(list.length);
  }, [userId, onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleVote = async (ideaId: string, hasVoted: boolean) => {
    if (hasVoted) {
      const { error } = await supabase
        .from("idea_votes")
        .delete()
        .eq("user_id", userId)
        .eq("idea_id", ideaId);
      if (error) {
        toast.error("Failed to remove vote");
        return;
      }
      setVotedIds((prev) => {
        const next = new Set(prev);
        next.delete(ideaId);
        return next;
      });
      // Removing support: drop from list
      setIdeas((prev) => {
        const next = prev?.filter((i) => i.id !== ideaId) ?? null;
        if (next) onCountChange?.(next.length);
        return next;
      });
    } else {
      const { error } = await supabase.from("idea_votes").insert({ user_id: userId, idea_id: ideaId });
      if (error) {
        toast.error("Failed to add vote");
        return;
      }
      setVotedIds((prev) => new Set(prev).add(ideaId));
      setIdeas((prev) =>
        prev?.map((i) => (i.id === ideaId ? { ...i, vote_count: i.vote_count + 1 } : i)) ?? null,
      );
    }
  };

  if (ideas === null) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-64 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  if (ideas.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border p-10 text-center">
        <p className="text-sm text-muted-foreground">
          You haven't supported any projects yet. Upvote ideas to track them here.
        </p>
        <Link to="/ideas">
          <Button size="sm" variant="outline">Browse Projects</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {ideas.map((idea) => (
        <IdeaCard
          key={idea.id}
          idea={idea}
          hasVoted={votedIds.has(idea.id)}
          onVote={(hasVoted) => handleVote(idea.id, hasVoted)}
          currentUserId={userId}
        />
      ))}
    </div>
  );
};

export default SupportingTab;
